import { CarOutlined, DownloadOutlined } from "@ant-design/icons";
import { Calculation } from "@rumble-it/common-lib/types/models/Calculation.interface";
import { Dossier } from "@rumble-it/common-lib/types/models/Dossier.interface";
import {
  Avatar,
  Col,
  Row,
  Space,
  Typography,
  type DescriptionsProps,
} from "antd";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { generateSixDigitNumber } from "../tabs/ClaimDossierCalculationTab";
import { fetchCalculationById } from "@/lib/api/resources/claims";
import { Modal, Descriptions } from "@/components/v2";
import { Button, Card, LicensePlate } from "@rumbledirect/ui";

const { Title, Text } = Typography;

export const ClaimDossierCalculationSummary = ({
  calculation,
  dossier,
}: {
  calculation: Calculation | null | undefined;
  dossier: Dossier | null | undefined;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: fullCalculation, isLoading: isCalculationLoading } = useQuery({
    queryKey: ["calculation", calculation?.id],
    queryFn: () => {
      if (!calculation?.id) {
        throw new Error("Calculation ID is undefined");
      }
      return fetchCalculationById(calculation!.id);
    },
    enabled: !!calculation?.id && isModalOpen,
  });
  return (
    <Card
      className="mb-6"
      extra={
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => setIsModalOpen(true)}
          disabled={!calculation?.id}
        >
          Bekijk Audatex calculatie
        </Button>
      }
    >
      <Row justify="space-between">
        <Col xxl={{ span: 8 }} span={12}>
          <Space>
            <Row gutter={12} align="middle">
              <Col flex="64px">
                <Avatar size={64} icon={<CarOutlined />} />
              </Col>

              <Col>
                <Title level={5} style={{ margin: 0 }}>
                  Calculatie #{generateSixDigitNumber(calculation?.id || "")}
                </Title>
                <Text type="secondary">
                  {dossier?.vehicle?.brand} — {dossier?.vehicle?.model}
                </Text>
                <div className="mt-2">
                  <LicensePlate country="NL">
                    {dossier?.vehicle?.licensePlate?.identifier}
                  </LicensePlate>
                </div>
              </Col>
            </Row>
          </Space>
        </Col>

        <Col xxl={{ span: 8 }} span={12}>
          {(() => {
            const items: DescriptionsProps["items"] = [
              { label: "Type calculatie", children: "Audatex" },
              {
                label: "Kenteken",
                children: dossier?.vehicle?.licensePlate?.identifier ?? null,
              },
              {
                label: "Kilometerstand",
                children: dossier?.kilometers
                  ? `${dossier.kilometers} km`
                  : null,
              },
            ];
            return <Descriptions items={items} />;
          })()}
        </Col>

        <Col xxl={{ span: 8, offset: 0 }} span={12} offset={12}>
          {(() => {
            const items: DescriptionsProps["items"] = [
              {
                label: "Calculatie gedaan door",
                children: (
                  <>
                    Schadenet Zoetermeer
                    <br />
                    De Werven 5, 2731 AZ Benthuizen <br />
                    079 341 2911
                  </>
                ),
              },
            ];
            return <Descriptions items={items} />;
          })()}
        </Col>
      </Row>

      <Modal
        title="Audatex calculatie"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={800}
        loading={isCalculationLoading}
      >
        <pre className="">{fullCalculation?.calculation}</pre>
      </Modal>
    </Card>
  );
};
